import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { fetchWithAuth } from '../lib/auth'
import { ChevronLeft, ChevronRight, User, TrendingUp, PieChart, BarChart3, Receipt, X } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from 'urql'
import { gql } from '../gql/gql'
import { useAuth } from '../context/auth-provider'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardFooter } from '../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog'
import { AddExpenseForm } from '../components/add-expense-form'
import { EditTransactionForm } from '../components/edit-transaction-form'
import { TransactionCard, TransactionData } from '../components/transaction-card'
import { Skeleton } from '../components/ui/skeleton'
import { Logo } from '../components/logo'
import { SpendingChart } from '../components/spending-chart'
import { CategoryChart } from '../components/category-chart'
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs'
import { MerchantChart } from '../components/merchant-chart'
import { BudgetCard } from '../components/budget-card'
import { EditProfileDialog } from '../components/edit-profile-dialog'
import { CurrencyOnboardingDialog } from '../components/currency-onboarding-dialog'
import { CurrencyPickerDialog } from '../components/currency-picker-dialog'
import { ChangePasswordDialog } from '../components/change-password-dialog'
import { FeedbackDialog } from '../components/feedback-dialog'
import { ImportCsvDialog } from '../components/import-csv-dialog'
import { SavingsReviewCard } from '../components/savings-review-card'
import { getCurrencySymbol } from '../lib/currencies'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'

const GET_ANALYTICS_QUERY = gql(`
  query GetDashboardAnalytics($year: Int!, $month: Int) {
    me {
      id
      name
      email
      monthlyBudget
      preferredCurrency
      emailVerified
      hasPassword
    }
    monthlySummary(year: $year, month: $month) {
      totalAmount
      budgetPace {
        message
        onTrack
      }
    }
    spendingOverTime(year: $year, month: $month) {
      date
      amount
    }
    spendingByCategory(year: $year, month: $month) {
      category
      amount
    }
    spendingByMerchant(year: $year, month: $month) {
      merchant
      amount
    }
  }
`)

const GET_FEED_QUERY = gql(`
  query GetDashboardFeed($first: Int) {
    transactions(first: $first) {
      edges {
        node {
          id
          merchant
          purchasedAt
          total
          receiptUrl
          receiptFilename
          receiptContentType
          receiptSize
          latitude
          longitude
          notes
          items {
            id
            name
            amount
            category
            purchasedAt
            merchant
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

const DELETE_ACCOUNT_MUTATION = gql(`
  mutation DeleteAccount {
    deleteAccount
  }
`)

const BANNER_KEY = 'email_forwarding_banner_dismissed'

export function DashboardPage() {
  const { setAccessToken } = useAuth()
  const navigate = useNavigate()
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    if (!localStorage.getItem(BANNER_KEY)) setShowBanner(true)
  }, [])

  const dismissBanner = () => {
    localStorage.setItem(BANNER_KEY, '1')
    setShowBanner(false)
  }
  const [, deleteAccount] = useMutation(DELETE_ACCOUNT_MUTATION)

  const handleDeleteAccount = async () => {
    if (!window.confirm('This will permanently delete your account and all your data. This cannot be undone.')) return
    const result = await deleteAccount({})
    if (result.error) {
      alert('Failed to delete account. Please try again.')
    } else {
      setAccessToken(null)
      navigate('/')
    }
  }

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [selectedTx, setSelectedTx] = useState<TransactionData | null>(null)
  const [isCurrencyPickerOpen, setIsCurrencyPickerOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8080'
      const response = await fetchWithAuth(`${apiBase}/export/csv`)
      if (!response.ok) throw new Error('Export failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `buylog-export-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      setExporting(false)
    }
  }

  const [activeChart, setActiveChart] = useState('trends')
  const [chartView, setChartView] = useState<'monthly' | 'yearly'>('monthly')
  const [chartDate, setChartDate] = useState(new Date())

  const chartYear = chartDate.getFullYear()
  const chartMonth = chartView === 'monthly' ? chartDate.getMonth() + 1 : null

  const handlePrev = () => {
    const newDate = new Date(chartDate)
    if (chartView === 'monthly') newDate.setMonth(newDate.getMonth() - 1)
    else newDate.setFullYear(newDate.getFullYear() - 1)
    setChartDate(newDate)
  }

  const handleNext = () => {
    const newDate = new Date(chartDate)
    if (chartView === 'monthly') newDate.setMonth(newDate.getMonth() + 1)
    else newDate.setFullYear(newDate.getFullYear() + 1)
    setChartDate(newDate)
  }

  const [{ data: analyticsData }, reexecuteAnalytics] = useQuery({
    query: GET_ANALYTICS_QUERY,
    variables: { year: chartYear, month: chartMonth },
    requestPolicy: 'cache-and-network',
  })

  const [{ data: feedData, fetching: fetchingFeed, error: feedError }, reexecuteFeed] = useQuery({
    query: GET_FEED_QUERY,
    variables: { first: 3 },
    requestPolicy: 'cache-and-network',
  })

  const refreshDashboard = () => {
    reexecuteAnalytics({ requestPolicy: 'network-only' })
    reexecuteFeed({ requestPolicy: 'network-only' })
  }

  const monthlyTotal = analyticsData?.monthlySummary?.totalAmount || 0
  const recentEdges = (feedData?.transactions?.edges ?? []) as { node: TransactionData }[]
  const hasMore = feedData?.transactions?.pageInfo?.hasNextPage

  const currencySymbol = getCurrencySymbol(analyticsData?.me?.preferredCurrency || 'USD')
  const currentBudget = analyticsData?.me?.monthlyBudget
  const needsCurrencySetup = analyticsData?.me && !analyticsData.me.preferredCurrency

  return (
    <div className="min-h-screen bg-background">
      <CurrencyOnboardingDialog
        open={!!needsCurrencySetup}
        name={analyticsData?.me?.name || ''}
      />
      <CurrencyPickerDialog
        open={isCurrencyPickerOpen}
        onOpenChange={setIsCurrencyPickerOpen}
        currentCurrency={analyticsData?.me?.preferredCurrency}
        name={analyticsData?.me?.name || ''}
      />
      <header className="border-b bg-primary/5 sticky top-0 z-10 backdrop-blur-md bg-background/80">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <Link to="/dashboard"><Logo /></Link>
          {showBanner && (
            <div className="hidden md:flex items-center gap-2 text-sm bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5">
              <span className="text-foreground">
                <span className="font-medium">New:</span> Forward receipts to{' '}
                <button className="font-mono font-medium hover:underline" onClick={() => navigator.clipboard.writeText('receipts@getbuylog.com')}>
                  receipts@getbuylog.com
                </button>
                {' '}and they log automatically.
              </span>
              <button onClick={dismissBanner} title="Dismiss — find it anytime in Edit Profile" className="text-muted-foreground hover:text-foreground ml-1">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
              <DialogTrigger asChild>
                <Button>Add Expense</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add expense</DialogTitle>
                </DialogHeader>
                <AddExpenseForm
                  currencySymbol={currencySymbol}
                  onSuccess={() => { setIsAddOpen(false); refreshDashboard() }}
                  onCancel={() => setIsAddOpen(false)}
                />
              </DialogContent>
            </Dialog>
            {analyticsData?.me && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden md:inline-block">{analyticsData.me.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="font-normal">
                    <p className="text-sm font-medium">{analyticsData.me.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{analyticsData.me.email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <EditProfileDialog
                    currentName={analyticsData.me.name}
                    currentEmail={analyticsData.me.email}
                    currentCurrency={analyticsData.me.preferredCurrency}
                  >
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      Edit profile
                    </DropdownMenuItem>
                  </EditProfileDialog>
                  <DropdownMenuItem onSelect={() => setIsCurrencyPickerOpen(true)}>
                    Currency: {analyticsData.me.preferredCurrency || 'USD'}
                  </DropdownMenuItem>
                  <ChangePasswordDialog hasPassword={analyticsData.me.hasPassword ?? true}>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      {analyticsData.me.hasPassword ? 'Change password' : 'Set password'}
                    </DropdownMenuItem>
                  </ChangePasswordDialog>
                  <FeedbackDialog>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      Send feedback
                    </DropdownMenuItem>
                  </FeedbackDialog>
                  <ImportCsvDialog onSuccess={refreshDashboard}>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      Import CSV
                    </DropdownMenuItem>
                  </ImportCsvDialog>
                  <DropdownMenuItem onSelect={handleExport} disabled={exporting}>
                    {exporting ? 'Exporting…' : 'Export CSV'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setAccessToken(null)} className="text-destructive focus:text-destructive">
                    Logout
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDeleteAccount} className="text-destructive focus:text-destructive">
                    Delete Account
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto p-4 md:p-6 max-w-4xl">
      <div className="mb-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <Tabs value={activeChart} onValueChange={setActiveChart} className="w-full md:w-auto">
                <TabsList className="grid w-full grid-cols-3 md:w-auto">
                  <TabsTrigger value="trends" className="gap-2"><TrendingUp className="h-4 w-4" /><span className="hidden sm:inline">Trends</span></TabsTrigger>
                  <TabsTrigger value="merchants" className="gap-2"><BarChart3 className="h-4 w-4" /><span className="hidden sm:inline">Merchants</span></TabsTrigger>
                  <TabsTrigger value="categories" className="gap-2"><PieChart className="h-4 w-4" /><span className="hidden sm:inline">Categories</span></TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handlePrev}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm font-medium min-w-[100px] text-center">
                    {chartView === 'monthly'
                      ? chartDate.toLocaleString('default', { month: 'long', year: 'numeric' })
                      : chartDate.getFullYear()}
                  </span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleNext}><ChevronRight className="h-4 w-4" /></Button>
                </div>

                <Tabs value={chartView} onValueChange={(v) => setChartView(v as 'monthly' | 'yearly')} className="w-auto">
                  <TabsList>
                    <TabsTrigger value="monthly">Monthly</TabsTrigger>
                    <TabsTrigger value="yearly">Yearly</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-2">
            {activeChart === 'trends' && (
              <SpendingChart
                data={analyticsData?.spendingOverTime || []}
                budget={currentBudget ? (chartView === 'yearly' ? currentBudget * 12 : currentBudget) : null}
                budgetLabel="Budget"
                currencySymbol={currencySymbol}
                year={chartYear}
                month={chartMonth}
                onDateClick={(date) => {
                  const [y, m] = date.split('-')
                  if (chartView === 'yearly') {
                    navigate(`/transactions?year=${y}&month=${m}`)
                  } else {
                    navigate(`/transactions?date=${date}`)
                  }
                }}
              />
            )}
            {activeChart === 'categories' && (
              <CategoryChart
                data={analyticsData?.spendingByCategory || []}
                currencySymbol={currencySymbol}
                onCategoryClick={(category) =>
                  navigate(`/transactions?category=${encodeURIComponent(category)}&year=${chartYear}${chartMonth ? `&month=${chartMonth}` : ''}`)
                }
              />
            )}
            {activeChart === 'merchants' && (
              <MerchantChart
                data={analyticsData?.spendingByMerchant || []}
                currencySymbol={currencySymbol}
                onMerchantClick={(merchant) =>
                  navigate(`/transactions?merchant=${encodeURIComponent(merchant)}&year=${chartYear}${chartMonth ? `&month=${chartMonth}` : ''}`)
                }
              />
            )}
          </CardContent>
          <CardFooter className="border-t px-4 py-3">
            <BudgetCard
              embedded
              budget={currentBudget}
              spent={monthlyTotal}
              currencySymbol={currencySymbol}
              chartView={chartView}
              chartDate={chartDate}
              budgetPace={analyticsData?.monthlySummary?.budgetPace ?? null}
              onSuccess={() => {}}
            />
          </CardFooter>
        </Card>
      </div>

      <div className="space-y-2">
        <SavingsReviewCard />

        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Recent</span>
            {(hasMore || recentEdges.length > 0) && (
              <button onClick={() => navigate('/transactions')} className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                Search & browse →
              </button>
            )}
          </div>

        {fetchingFeed && recentEdges.length === 0 && (
          <div className="grid gap-2">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="flex items-start justify-between p-3 gap-4">
                  <div className="space-y-2"><Skeleton className="h-4 w-[150px]" /><Skeleton className="h-3 w-[100px]" /></div>
                  <div className="space-y-2 flex flex-col items-end"><Skeleton className="h-5 w-[80px]" /><Skeleton className="h-3 w-[60px]" /></div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {feedError && <div className="text-red-500 text-sm">Error: {feedError.message}</div>}

        {!fetchingFeed && !feedError && recentEdges.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/30">
            <Receipt className="h-10 w-10 mb-3 text-muted-foreground opacity-40" />
            <h3 className="font-semibold text-base mb-1">No expenses yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Add your first expense to start tracking your spending.</p>
            <Button onClick={() => setIsAddOpen(true)}>Add your first expense</Button>
          </div>
        )}

          <div className="grid gap-2">
            {recentEdges.map((edge) => {
              const tx = edge.node
              return tx ? (
                <TransactionCard
                  key={tx.id}
                  transaction={tx}
                  currencySymbol={currencySymbol}
                  onClick={() => setSelectedTx(tx)}
                />
              ) : null
            })}
          </div>
        </div>
      </div>

      {/* Edit transaction dialog */}
      <Dialog open={!!selectedTx} onOpenChange={(open) => !open && setSelectedTx(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTx?.merchant ?? 'Edit expense'}</DialogTitle>
          </DialogHeader>
          {selectedTx && (
            <EditTransactionForm
              transaction={selectedTx}
              currencySymbol={currencySymbol}
              onSuccess={() => {
                setSelectedTx(null)
                refreshDashboard()
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      </main>

      <footer className="border-t mt-8">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl py-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>© {new Date().getFullYear()} Buylog</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <a href="mailto:feedback@getbuylog.com" className="hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}